import React, { useState, useEffect, useRef } from 'react';
import { listenToDriverLocations, listenToRides } from '../firebase/database';

export default function LiveMapPage() {
  const [driverLocations, setDriverLocations] = useState([]);
  const [rides, setRides]                     = useState([]);
  const [selected, setSelected]               = useState(null);
  const [panelOpen, setPanelOpen]             = useState(false); // mobile: drawer closed by default
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});

  useEffect(() => {
    const unsub1 = listenToDriverLocations(setDriverLocations);
    const unsub2 = listenToRides(setRides);
    return () => { unsub1(); unsub2(); };
  }, []);

  // Load Google Maps
  useEffect(() => {
    if (window.google || document.getElementById('gmaps-script')) return;
    const script = document.createElement('script');
    script.id = 'gmaps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyApxfMo84d9IQkWbI4yRwdbGEdhu7EtN8s&libraries=places&callback=initAdminMap`;
    script.async = true;
    window.initAdminMap = () => initMap();
    document.head.appendChild(script);
  }, []);

  const initMap = () => {
    if (!mapRef.current || mapInstanceRef.current) return;
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 41.8781, lng: -87.6298 }, // Chicago, IL
      zoom: 11,
      styles: DARK_MAP_STYLE,
      disableDefaultUI: true,
      zoomControl: true,
      fullscreenControl: true,
    });
  };

  useEffect(() => {
    if (!mapInstanceRef.current || !window.google) return;
    driverLocations.forEach(driver => {
      if (!driver.latitude || !driver.longitude) return;
      if (markersRef.current[driver.driverId]) {
        markersRef.current[driver.driverId].setPosition({ lat: driver.latitude, lng: driver.longitude });
      } else {
        const marker = new window.google.maps.Marker({
          position: { lat: driver.latitude, lng: driver.longitude },
          map: mapInstanceRef.current,
          icon: {
            path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
            scale: 6,
            fillColor: driver.isOnline ? '#22c55e' : '#94a3b8',
            fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2,
          },
          title: driver.driverName,
        });
        marker.addListener('click', () => { setSelected(driver); setPanelOpen(true); });
        markersRef.current[driver.driverId] = marker;
      }
    });
  }, [driverLocations]);

  const onlineCount  = driverLocations.filter(d => d.isOnline).length;
  const activeRides  = rides.filter(r => ['assigned','accepted','in_progress'].includes(r.status)).length;
  const onRideCount  = driverLocations.filter(d => rides.some(r => r.driverId === d.driverId && ['assigned','accepted','in_progress'].includes(r.status))).length;

  const focusDriver = (driver) => {
    if (mapInstanceRef.current && driver.latitude && window.google) {
      mapInstanceRef.current.panTo({ lat: driver.latitude, lng: driver.longitude });
      mapInstanceRef.current.setZoom(15);
    }
    setSelected(driver);
  };

  return (
    <div style={{ position: 'relative', height: 'calc(100vh - 120px)', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
      <style>{`
        @media (max-width: 768px) {
          .map-desktop-panel { display: none !important; }
          .map-mobile-fab { display: flex !important; }
          .map-mobile-drawer { display: flex !important; }
        }
        @media (min-width: 769px) {
          .map-desktop-panel { display: flex !important; }
          .map-mobile-fab { display: none !important; }
          .map-mobile-drawer { display: none !important; }
        }
      `}</style>

      {/* ── Map fills full container ── */}
      <div ref={mapRef} style={{ position: 'absolute', inset: 0 }} />

      {/* Map loading placeholder */}
      {!window.google && (
        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', background: '#1a2332',
          color: '#64748b', fontSize: 14, gap: 8, zIndex: 1
        }}>
          <div style={{ fontSize: 36 }}>🗺</div>
          <div style={{ fontWeight: 600, color: '#94a3b8' }}>Map loading...</div>
        </div>
      )}

      {/* ── Stats overlay (top-left, always visible) ── */}
      <div style={{
        position: 'absolute', top: 12, left: 12, zIndex: 10,
        display: 'flex', gap: 8, flexWrap: 'wrap'
      }}>
        {[
          { label: 'Online', value: onlineCount, color: '#22c55e' },
          { label: 'On Ride', value: onRideCount, color: '#f59e0b' },
          { label: 'Total', value: driverLocations.length, color: '#94a3b8' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'rgba(15,23,42,0.85)', backdropFilter: 'blur(8px)',
            borderRadius: 10, padding: '6px 12px', textAlign: 'center',
            border: `1px solid ${s.color}40`
          }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
            <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* ── Desktop: right-side panel ── */}
      <div className="map-desktop-panel" style={{
        display: 'none',
        position: 'absolute', top: 0, right: 0, bottom: 0, width: 260,
        background: 'rgba(15,23,42,0.92)', backdropFilter: 'blur(12px)',
        flexDirection: 'column', zIndex: 10,
        borderLeft: '1px solid rgba(255,255,255,0.06)'
      }}>
        <div style={{ padding: '16px 14px 10px', borderBottom: '1px solid #1e293b' }}>
          <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 14 }}>Live Driver Tracking</div>
          <div style={{ color: '#64748b', fontSize: 11, marginTop: 3 }}>
            {onlineCount} online · {activeRides} active rides
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '6px 0' }}>
          {driverLocations.length === 0 && (
            <div style={{ color: '#475569', fontSize: 12, textAlign: 'center', padding: 20 }}>
              No drivers broadcasting location
            </div>
          )}
          {driverLocations.map(driver => {
            const activeRide = rides.find(r => r.driverId === driver.driverId && ['assigned','accepted','in_progress'].includes(r.status));
            const isSelected = selected?.driverId === driver.driverId;
            return (
              <div key={driver.driverId} onClick={() => focusDriver(driver)} style={{
                padding: '10px 14px', cursor: 'pointer',
                background: isSelected ? '#1e293b' : 'transparent',
                borderLeft: isSelected ? '3px solid #f59e0b' : '3px solid transparent',
                transition: 'all 0.15s'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: driver.isOnline ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.1)',
                    border: `1.5px solid ${driver.isOnline ? '#22c55e' : '#475569'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 12, color: driver.isOnline ? '#22c55e' : '#64748b', flexShrink: 0
                  }}>
                    {driver.driverName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 13 }}>{driver.driverName}</div>
                    <div style={{ fontSize: 10, color: driver.isOnline ? '#22c55e' : '#475569' }}>
                      {driver.isOnline ? '● Online' : '● Offline'}
                    </div>
                  </div>
                </div>
                {activeRide && (
                  <div style={{
                    marginTop: 6, padding: '4px 8px', background: 'rgba(245,158,11,0.1)',
                    borderRadius: 6, fontSize: 11, color: '#f59e0b'
                  }}>
                    🚗 {activeRide.customerName}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{ padding: '10px 14px', borderTop: '1px solid #1e293b' }}>
          {[['#22c55e', 'Online'], ['#94a3b8', 'Offline'], ['#f59e0b', 'On Ride']].map(([color, label]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
              <span style={{ fontSize: 11, color: '#64748b' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Mobile: FAB to open drawer ── */}
      <button
        className="map-mobile-fab"
        onClick={() => setPanelOpen(true)}
        style={{
          display: 'none',
          position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#0f172a', color: '#f1f5f9', border: 'none',
          borderRadius: 24, padding: '12px 24px', fontSize: 13, fontWeight: 600,
          cursor: 'pointer', zIndex: 20, gap: 8, alignItems: 'center',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
        }}
      >
        <span style={{ fontSize: 16 }}>👥</span>
        {onlineCount} driver{onlineCount !== 1 ? 's' : ''} online
        {onlineCount > 0 && (
          <span style={{
            background: '#22c55e', color: '#fff', borderRadius: 10,
            padding: '1px 7px', fontSize: 11, fontWeight: 700
          }}>{onlineCount}</span>
        )}
      </button>

      {/* ── Mobile: Bottom Drawer ── */}
      {panelOpen && (
        <div
          className="map-mobile-drawer"
          style={{
            display: 'none',
            position: 'absolute', bottom: 0, left: 0, right: 0,
            background: '#0f172a', borderRadius: '16px 16px 0 0',
            zIndex: 30, flexDirection: 'column',
            maxHeight: '55%', border: '1px solid #1e293b',
            boxShadow: '0 -8px 32px rgba(0,0,0,0.4)'
          }}
        >
          {/* Drag handle */}
          <div style={{ padding: '10px 0 0', display: 'flex', justifyContent: 'center' }}>
            <div style={{ width: 40, height: 4, borderRadius: 2, background: '#334155' }} />
          </div>
          <div style={{ padding: '8px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <span style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 14 }}>Drivers</span>
              <span style={{ color: '#64748b', fontSize: 12, marginLeft: 8 }}>{onlineCount} online · {activeRides} active</span>
            </div>
            <button onClick={() => setPanelOpen(false)} style={{
              background: '#1e293b', border: 'none', borderRadius: '50%',
              width: 28, height: 28, color: '#94a3b8', cursor: 'pointer', fontSize: 16
            }}>×</button>
          </div>

          <div style={{ overflowY: 'auto', flex: 1, paddingBottom: 12 }}>
            {driverLocations.length === 0 && (
              <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 20 }}>
                No drivers broadcasting location
              </div>
            )}
            {driverLocations.map(driver => {
              const activeRide = rides.find(r => r.driverId === driver.driverId && ['assigned','accepted','in_progress'].includes(r.status));
              return (
                <div key={driver.driverId} onClick={() => { focusDriver(driver); setPanelOpen(false); }} style={{
                  padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12,
                  borderBottom: '1px solid #1e293b'
                }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: '50%',
                    background: driver.isOnline ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.1)',
                    border: `2px solid ${driver.isOnline ? '#22c55e' : '#475569'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 14, color: '#f1f5f9', flexShrink: 0
                  }}>
                    {driver.driverName?.charAt(0)?.toUpperCase()}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>{driver.driverName}</div>
                    {activeRide ? (
                      <div style={{ fontSize: 12, color: '#f59e0b' }}>🚗 On ride: {activeRide.customerName}</div>
                    ) : (
                      <div style={{ fontSize: 12, color: driver.isOnline ? '#22c55e' : '#475569' }}>
                        {driver.isOnline ? 'Online' : 'Offline'}
                      </div>
                    )}
                  </div>
                  <span style={{ fontSize: 18, color: '#475569' }}>›</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const DARK_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#212121' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#383838' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#484848' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
];
